import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Trophy, Medal } from 'lucide-react';
import { fetchWeekBoard } from '../../../lib/motwBoard';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Badge } from '../../ui/badge';
import { cn } from '../../../lib/utils';

// 135.6015625 -> "2:15.602"
const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toFixed(3).padStart(6, '0')}`;
};

const RankBadge = ({ rank }) => {
  if (rank === 1) return <Trophy className="h-4 w-4 text-warning" aria-label="1st" />;
  if (rank === 2) return <Medal className="h-4 w-4 text-muted-foreground" aria-label="2nd" />;
  if (rank === 3) return <Medal className="h-4 w-4 text-primary/70" aria-label="3rd" />;
  return <span className="tabular-nums text-muted-foreground">{rank}</span>;
};

// One board for one mode. Everyone ran the same course, so day and night times
// sit on a single board — the session column just shows when someone ran.
const MotwLeaderboard = ({ week, mode, players, points }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const board = await fetchWeekBoard({ week, players, mode, points });
      if (!cancelled) {
        setRows(board);
        setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [week, mode, players, points]);

  if (players.length === 0) {
    return (
      <div className="rounded-md border border-dashed px-6 py-12 text-center text-muted-foreground">
        No players have joined yet. Link your Steam profile above to appear on the board.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: Math.min(players.length, 5) }).map((_, i) => (
          <div key={i} className="h-10 animate-pulse rounded-md border bg-muted/40" />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed px-6 py-12 text-center text-muted-foreground">
        No times yet. Only runs set inside a session window count.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">#</TableHead>
            <TableHead>Player</TableHead>
            <TableHead>Session</TableHead>
            <TableHead className="text-right">Time</TableHead>
            <TableHead className="w-20 text-right">Points</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.uid} className={cn(row.rank === 1 && 'bg-warning/5')}>
              <TableCell>
                <RankBadge rank={row.rank} />
              </TableCell>
              <TableCell className="font-medium">
                <RouterLink to={`/user/${row.uid}`} className="hover:text-primary hover:underline">
                  {row.name}
                </RouterLink>
              </TableCell>
              <TableCell>
                <Badge variant="outline">{row.sessionLabel}</Badge>
              </TableCell>
              <TableCell className="text-right font-mono tabular-nums">
                {formatTime(row.timeSeconds)}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {row.points > 0 ? row.points : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default MotwLeaderboard;
